/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { X } from 'lucide-react'
import React, { useMemo, useRef, useState } from 'react'

import { ActivityCarousel } from '@/components/quality-explorer/carousel/ActivityCarousel'
import { FilterableKit, kitFilters } from '@/components/quality-explorer/carousel/activity-carousel-filters'
import { Activity } from '@/components/quality-explorer/types'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ActivityCarouselProps = {
  /** The kit to show the filters for. */
  kit: FilterableKit

  /** The activities to display in the carousel. */
  activities: Array<Activity>
}

/**
 * A wrapper for the activity carousel that includes the filter cards for the specific kits.
 */
const ActivityCarouselWithFilters: React.FC<ActivityCarouselProps> = ({ kit, activities }) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)
  const filters = kitFilters[kit]

  // The filtered list is derived rather than stored in state, so it stays correct if the activities prop changes. It
  // is memoised so the carousel only resets its position when the filter (or the activities themselves) change.
  const filteredActivities = useMemo(() => {
    const filter = filters.find((f) => f.title === selectedFilter)
    return filter ? activities.filter((activity) => filter.activities.includes(activity.alias_p)) : activities
  }, [activities, filters, selectedFilter])

  /** How many of the kit's activities each filter would show, displayed on the filter cards. */
  const filterCounts = useMemo(() => {
    return new Map(
      filters.map((filter) => [
        filter.title,
        activities.filter((activity) => filter.activities.includes(activity.alias_p)).length,
      ]),
    )
  }, [activities, filters])

  /** Select a filter and bring the carousel into view so the effect of the click is visible. */
  const selectFilter = (title: string) => {
    setSelectedFilter(title)
    carouselRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    })
  }

  return (
    <div className={'relative w-full max-w-7xl py-8'}>
      <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'}>
        {filters.map((filter) => {
          const Icon = filter.icon
          const count = filterCounts.get(filter.title) ?? 0
          return (
            <div
              key={filter.title}
              className={cn('cursor-pointer h-full')}
              onClick={() => {
                if (selectedFilter === filter.title) setSelectedFilter(null)
                else selectFilter(filter.title)
              }}
            >
              <div
                className={cn(
                  'h-full flex flex-col border-4 border-transparent shadow-mta p-4 rounded-2xl hover:shadow-lg',
                  selectedFilter === filter.title && 'border-secondary-500 bg-secondary-100',
                )}
              >
                <div className={'flex flex-row items-center gap-4'}>
                  <div className={'flex items-center justify-center w-12 h-12 bg-secondary-500 rounded-full shadow-lg'}>
                    <Icon />
                  </div>
                  <h3 className={'text-xl text-primary-500 font-semibold'}>{filter.title}</h3>
                </div>
                <p className={'mt-4 text-sm text-graphite-500'}>{filter.description}</p>
                <p className={'mt-auto pt-3 text-sm font-medium text-graphite-500'}>
                  {count} {count === 1 ? 'activity' : 'activities'}
                </p>
              </div>
            </div>
          )
        })}
      </div>

      {/* The scroll-margins mirror the sticky-header offsets in globals.css so scrolling here lands below it. */}
      <div ref={carouselRef} className={'scroll-mt-32 min-[801px]:scroll-mt-[9.25rem]'}>
        {selectedFilter && (
          <div className={'mt-6 flex justify-center'}>
            <button
              type={'button'}
              onClick={() => setSelectedFilter(null)}
              className={cn(
                'flex items-center gap-2 rounded-full border-2 border-secondary-500 bg-secondary-100 px-4 py-1.5',
                'text-sm font-medium text-primary-500 hover:bg-secondary-500/20 transition-colors',
              )}
            >
              {selectedFilter}
              <X className={'w-4 h-4'} aria-hidden={true} />
              <span className={'sr-only'}>Clear filter</span>
            </button>
          </div>
        )}
        <ActivityCarousel activities={filteredActivities} />
      </div>
    </div>
  )
}

export { ActivityCarouselWithFilters }
